import { createClient } from "@supabase/supabase-js";
import "../styles.css";
import "./auth.css";
import "./calendar.css";
import "./appearance.css";
import "./toast.css";
import { applyAppearance, loadAppearance } from "./appearance.js";

const API_BASE = (import.meta.env.VITE_KAHVOR_API_BASE || "https://api.kahvor.com").replace(/\/$/, "");
const root = document.querySelector("#app");
const PUBLIC_CONFIG_CACHE_KEY = "kahvor:public-config:v1";

function validPublicConfig(value) {
  return Boolean(value?.supabaseUrl && (value?.supabaseAnonKey || value?.supabasePublishableKey));
}

function installMailRealtimeBridge(supabase, userId, initialAccessToken = "") {
  const candidateUserId = String(userId || "").trim();
  const safeUserId = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(candidateUserId)
    ? candidateUserId
    : "";
  const channels = new Set();
  let accessToken = String(initialAccessToken || "");

  const authenticateRealtime = () => {
    if (!accessToken) return Promise.resolve();
    return Promise.resolve().then(() => supabase.realtime.setAuth(accessToken));
  };

  const disconnect = () => {
    const pending = [...channels].map((channel) => {
      channels.delete(channel);
      return Promise.resolve(supabase.removeChannel(channel)).catch(() => {});
    });
    return Promise.all(pending);
  };

  window.KAHVOR_REALTIME = {
    available: Boolean(safeUserId),
    table: "mail_client_change_events",
    async setAccessToken(nextAccessToken) {
      accessToken = String(nextAccessToken || "");
      if (!accessToken) {
        await disconnect();
        return;
      }
      await authenticateRealtime().catch(() => {});
    },
    subscribeMailChanges({ onEvent, onStatus } = {}) {
      if (!safeUserId || !accessToken) {
        queueMicrotask(() => onStatus?.("SIGNED_OUT"));
        return () => {};
      }

      let active = true;
      const suffix = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      let channel = null;
      authenticateRealtime().then(() => {
        if (!active) return;
        channel = supabase
          .channel(`mail-client-change-events:${safeUserId}:${suffix}`)
          .on(
            "postgres_changes",
            {
              event: "INSERT",
              schema: "public",
              table: "mail_client_change_events",
              filter: `user_id=eq.${safeUserId}`
            },
            (payload) => {
              if (!active || !payload?.new) return;
              onEvent?.(payload.new);
            }
          )
          .subscribe((status, error) => {
            if (active) onStatus?.(status, error || null);
          });
        channels.add(channel);
      }).catch((error) => {
        if (active) onStatus?.("CHANNEL_ERROR", error);
      });
      return () => {
        if (!active) return;
        active = false;
        if (channel) {
          channels.delete(channel);
          Promise.resolve(supabase.removeChannel(channel)).catch(() => {});
        }
      };
    },
    subscribeShipmentChanges({ onEvent, onStatus } = {}) {
      if (!safeUserId || !accessToken) {
        queueMicrotask(() => onStatus?.("SIGNED_OUT"));
        return () => {};
      }

      let active = true;
      const suffix = globalThis.crypto?.randomUUID?.() || `${Date.now()}-shipments`;
      let channel = null;
      authenticateRealtime().then(() => {
        if (!active) return;
        channel = supabase
          .channel(`shipment-change-log:${safeUserId}:${suffix}`)
          .on(
            "postgres_changes",
            {
              event: "INSERT",
              schema: "public",
              table: "shipment_change_log"
            },
            (payload) => {
              if (!active || !payload?.new) return;
              onEvent?.(payload.new);
            }
          )
          .subscribe((status, error) => {
            if (active) onStatus?.(status, error || null);
          });
        channels.add(channel);
      }).catch((error) => {
        if (active) onStatus?.("CHANNEL_ERROR", error);
      });
      return () => {
        if (!active) return;
        active = false;
        if (channel) {
          channels.delete(channel);
          Promise.resolve(supabase.removeChannel(channel)).catch(() => {});
        }
      };
    },
    disconnect
  };
  authenticateRealtime().catch(() => {});
}

function readCachedPublicConfig() {
  try {
    const cached = JSON.parse(localStorage.getItem(PUBLIC_CONFIG_CACHE_KEY) || "null");
    return validPublicConfig(cached?.value) ? cached.value : null;
  } catch {
    return null;
  }
}

async function fetchPublicConfig() {
  const response = await fetch(`${API_BASE}/api/public-config`, {
    headers: { Accept: "application/json" },
    cache: "no-cache"
  });
  if (!response.ok) throw new Error(`Configuration request failed (${response.status})`);
  const config = await response.json();
  if (!validPublicConfig(config)) throw new Error("Authentication configuration is incomplete");
  try {
    localStorage.setItem(PUBLIC_CONFIG_CACHE_KEY, JSON.stringify({ savedAt: Date.now(), value: config }));
  } catch {
    // The public configuration is safe to fetch again when storage is unavailable.
  }
  return config;
}

function cleanAuthUrl({ preserveNative = false } = {}) {
  const url = new URL(window.location.href);
  ["code", "error", "error_code", "error_description", "auth", "provider", "status", "account"].forEach((key) => url.searchParams.delete(key));
  if (preserveNative) url.searchParams.set("native", "1");
  url.pathname = "/";
  history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
}

function authMessage(message, tone = "") {
  const target = document.querySelector("[data-auth-message]");
  if (!target) return;
  target.textContent = message;
  target.dataset.tone = tone;
}

function appHomePath() {
  const url = new URL("/", location.origin);
  const current = new URLSearchParams(location.search);
  if (current.get("view") === "ios") url.searchParams.set("view", "ios");
  if (current.get("view") === "desktop") url.searchParams.set("view", "desktop");
  if (current.get("native") === "1") url.searchParams.set("native", "1");
  return `${url.pathname}${url.search}`;
}

function authCopy(mode) {
  if (mode === "create") return {
    eyebrow: "Create your Kahvor account",
    title: "One command account. Every inbox.",
    detail: "Create a provider-neutral Kahvor account, then connect the mail and calendar accounts you choose.",
    submit: "Create Kahvor account"
  };
  if (mode === "recover") return {
    eyebrow: "Account recovery",
    title: "Reset your Kahvor password.",
    detail: "We’ll send a secure recovery link to the email address for your Kahvor account.",
    submit: "Send recovery link"
  };
  if (mode === "confirm") return {
    eyebrow: "Confirm your account",
    title: "Check your email.",
    detail: "Open the confirmation link to finish creating your Kahvor account. If it does not arrive, request another below.",
    submit: "Resend confirmation email"
  };
  if (mode === "update") return {
    eyebrow: "Account recovery",
    title: "Choose a new password.",
    detail: "This changes only your Kahvor sign-in. Your connected mail-account passwords are never stored here.",
    submit: "Update password"
  };
  return {
    eyebrow: "Kahvor Command",
    title: "Your work, intelligently connected.",
    detail: "Sign in to your Kahvor account. Add Google, Outlook, Yahoo, iCloud, and other inboxes after sign-in.",
    submit: "Sign in to Kahvor"
  };
}

function authScreen({ mode = "signin", busy = false, error = "", notice = "", values = {} } = {}) {
  const installRequested = new URLSearchParams(location.search).get("install") === "1";
  const ios = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const installHelp = installRequested ? `<aside class="auth-install" role="note"><strong>${ios ? "Install Halo on this iPhone" : "Install Halo on this device"}</strong><p>${ios ? "In Safari, tap Share, choose Add to Home Screen, then tap Add." : "Use your browser’s Install or Add to Dock command after signing in."}</p></aside>` : "";
  const copy = authCopy(mode);
  const email = String(values.email || "").replace(/"/g, "&quot;");
  const name = String(values.name || "").replace(/"/g, "&quot;");
  const isCreate = mode === "create";
  const isRecovery = mode === "recover";
  const isConfirm = mode === "confirm";
  const isUpdate = mode === "update";
  const submitLabel = busy
    ? isCreate ? "Creating account…" : isRecovery ? "Sending link…" : isConfirm ? "Resending email…" : isUpdate ? "Updating password…" : "Signing in…"
    : copy.submit;
  root.innerHTML = `
    <main class="auth-stage">
      <section class="auth-card" aria-labelledby="auth-title">
        <img class="auth-mark" src="/assets/halo-mark.svg" alt="">
        <div class="auth-eyebrow">${copy.eyebrow}</div>
        <h1 id="auth-title">${copy.title}</h1>
        <p>${copy.detail}</p>
        ${!isRecovery && !isConfirm && !isUpdate ? `<div class="auth-tabs" role="tablist" aria-label="Kahvor account access">
          <button type="button" role="tab" aria-selected="${!isCreate}" class="${!isCreate ? "active" : ""}" data-auth-mode="signin">Sign in</button>
          <button type="button" role="tab" aria-selected="${isCreate}" class="${isCreate ? "active" : ""}" data-auth-mode="create">Create account</button>
        </div>` : ""}
        <form class="auth-form" data-auth-form data-mode="${mode}">
          ${isCreate ? `<label><span>Name</span><input name="name" type="text" autocomplete="name" value="${name}" required maxlength="80" placeholder="Your name"></label>` : ""}
          ${!isUpdate ? `<label><span>Kahvor account email</span><input name="email" type="email" inputmode="email" autocomplete="${isCreate ? "email" : "username"}" value="${email}" required ${isConfirm ? "readonly" : ""} placeholder="you@example.com"></label>` : ""}
          ${!isRecovery && !isConfirm ? `<label><span>${isUpdate ? "New password" : "Password"}</span><input name="password" type="password" autocomplete="${isCreate ? "new-password" : isUpdate ? "new-password" : "current-password"}" required minlength="8" placeholder="${isUpdate ? "At least 8 characters" : "Your Kahvor password"}"></label>` : ""}
          ${isCreate ? `<label><span>Confirm password</span><input name="confirmPassword" type="password" autocomplete="new-password" required minlength="8" placeholder="Repeat your Kahvor password"></label>` : ""}
          ${isUpdate ? `<label><span>Confirm new password</span><input name="confirmPassword" type="password" autocomplete="new-password" required minlength="8" placeholder="Repeat your new password"></label>` : ""}
          <button class="auth-primary" type="submit" ${busy ? "disabled" : ""}>${submitLabel}</button>
        </form>
        ${mode === "signin" ? `<button class="auth-text-action" type="button" data-auth-mode="recover">Forgot your Kahvor password?</button>` : ""}
        ${isRecovery ? `<button class="auth-text-action" type="button" data-auth-mode="signin">Back to sign in</button>` : ""}
        ${isConfirm ? `<div class="auth-secondary-actions"><button class="auth-text-action" type="button" data-auth-mode="signin">Back to sign in</button><button class="auth-text-action" type="button" data-auth-mode="recover">Use account recovery</button></div>` : ""}
        <aside class="auth-provider-note" role="note"><strong>Mail accounts are connected separately.</strong><p>Your Kahvor login does not have to match any inbox you add.</p></aside>
        ${installHelp}
        <p class="auth-message" data-auth-message data-tone="${error ? "error" : notice ? "success" : ""}" role="status" aria-live="polite">${error || notice}</p>
        <footer><a href="/legal/privacy.html">Privacy</a><a href="/legal/terms.html">Terms</a><a href="/legal/security.html">Security</a></footer>
      </section>
    </main>`;
}

function unavailableScreen(message) {
  authScreen({ error: message });
  const form = document.querySelector("[data-auth-form]");
  form?.setAttribute("hidden", "");
  const button = document.createElement("button");
  button.className = "auth-primary";
  button.type = "button";
  button.textContent = "Retry account services";
  button.addEventListener("click", () => location.reload());
  form?.after(button);
}

function readAuthValues(form) {
  const data = new FormData(form);
  return {
    name: String(data.get("name") || "").trim(),
    email: String(data.get("email") || "").trim(),
    password: String(data.get("password") || ""),
    confirmPassword: String(data.get("confirmPassword") || "")
  };
}

function authErrorMessage(error) {
  const message = String(error?.message || "Kahvor could not complete that request.");
  if (/invalid login credentials/i.test(message)) return "The email or password is incorrect. Use account recovery if this Kahvor account previously used Google sign-in.";
  if (/email not confirmed/i.test(message)) return "Confirm your Kahvor account from the email we sent, then sign in.";
  if (/user already registered/i.test(message)) return "A Kahvor account already uses this email. Sign in or reset its password.";
  if (/passwords do not match/i.test(message)) return "The passwords do not match. Enter the same password in both fields.";
  if (/password/i.test(message) && /weak|short|character/i.test(message)) return "Choose a stronger password with at least 8 characters.";
  return message;
}

function bindAuthScreen(supabase, initialMode = "signin", initial = {}) {
  let mode = initialMode;
  const show = (nextMode, options = {}) => {
    mode = nextMode;
    authScreen({ mode, ...options });
    wire();
  };
  const wire = () => {
    document.querySelectorAll("[data-auth-mode]").forEach((button) => {
      button.addEventListener("click", () => {
        const form = document.querySelector("[data-auth-form]");
        show(button.dataset.authMode || "signin", { values: form ? readAuthValues(form) : {} });
      });
    });
    document.querySelector("[data-auth-form]")?.addEventListener("submit", async (event) => {
      event.preventDefault();
      const values = readAuthValues(event.currentTarget);
      show(mode, { busy: true, values });
      try {
        if (mode === "create") {
          if (values.password !== values.confirmPassword) throw new Error("The passwords do not match.");
          const { data, error } = await supabase.auth.signUp({
            email: values.email,
            password: values.password,
            options: {
              emailRedirectTo: `${location.origin}/auth/confirmed/`,
              data: { display_name: values.name }
            }
          });
          if (error) throw error;
          if (data.session) {
            location.assign(appHomePath());
            return;
          }
          if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
            show("recover", {
              values: { email: values.email },
              notice: "This email may already have a Kahvor account. Send an account-access link to set its password safely."
            });
            return;
          }
          show("confirm", {
            values: { email: values.email },
            notice: "A confirmation email was requested. Check Inbox and Spam, or resend it here after one minute."
          });
          return;
        }
        if (mode === "confirm") {
          const { error } = await supabase.auth.resend({
            type: "signup",
            email: values.email,
            options: {
              emailRedirectTo: `${location.origin}/auth/confirmed/`
            }
          });
          if (error) throw error;
          show("confirm", {
            values: { email: values.email },
            notice: "Confirmation email requested again. Check Inbox and Spam before trying another resend."
          });
          return;
        }
        if (mode === "recover") {
          const { error } = await supabase.auth.resetPasswordForEmail(values.email, {
            redirectTo: `${location.origin}/auth/recovery/`
          });
          if (error) throw error;
          show("recover", {
            values: { email: values.email },
            notice: "If a Kahvor account uses this email, its recovery link is on the way."
          });
          return;
        }
        if (mode === "update") {
          if (values.password !== values.confirmPassword) throw new Error("The new passwords do not match.");
          const { error } = await supabase.auth.updateUser({ password: values.password });
          if (error) throw error;
          authScreen({ mode: "update", notice: "Your Kahvor password is updated. Opening Kahvor…" });
          setTimeout(() => location.assign(appHomePath()), 650);
          return;
        }
        const { error } = await supabase.auth.signInWithPassword({
          email: values.email,
          password: values.password
        });
        if (error) throw error;
        location.assign(appHomePath());
      } catch (error) {
        show(mode, { values, error: authErrorMessage(error) });
      }
    });
  };
  authScreen({ mode, ...initial });
  wire();
}

async function bootstrap() {
  const localHost = ["localhost", "127.0.0.1"].includes(location.hostname);
  const qaSessionToken = typeof window.__KAHVOR_QA_SESSION_TOKEN === "string"
    ? window.__KAHVOR_QA_SESSION_TOKEN
    : "";
  const preview = localHost
    && new URLSearchParams(location.search).get("preview") === "1"
    && !qaSessionToken;
  if (preview) {
    applyAppearance(loadAppearance(globalThis.localStorage, {
      legacySettings: (() => {
        try { return JSON.parse(localStorage.getItem("kahvor:settings") || "{}"); } catch { return {}; }
      })()
    }));
    window.KAHVOR_RUNTIME_CONFIG = {
      apiBase: API_BASE,
      accessToken: "",
      providerReturnUrl: `${location.origin}/auth/provider-complete/`,
      user: null,
      nativePlatform: /KahvorDesktop/i.test(navigator.userAgent) ? "macos" : "ios",
      notifications: {},
      preview: true
    };
    window.KAHVOR_AUTH = {
      async signOut() {
        await window.__kahvorClearRuntimeCaches?.();
        location.assign("/?preview=1");
      },
      getUser: () => null
    };
    await import("../app.js");
    return;
  }

  let config = readCachedPublicConfig();
  const configRequest = fetchPublicConfig();
  try {
    if (!config) config = await configRequest;
  } catch (error) {
    unavailableScreen(`Kahvor could not reach account services. ${error.message}`);
    return;
  }
  if (config) configRequest.catch(() => {});

  const supabase = createClient(config.supabaseUrl, config.supabaseAnonKey || config.supabasePublishableKey, {
    auth: {
      flowType: "pkce",
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true
    }
  });

  const nativeHandler = globalThis.webkit?.messageHandlers?.kahvorAuth;
  const nativePlatform = /KahvorDesktop/i.test(navigator.userAgent) ? "macos" : "ios";
  let session = null;
  let verifiedUser = null;
  if (localHost && qaSessionToken) {
    session = { access_token: qaSessionToken };
    verifiedUser = { id: "qa-user", email: "qa@kahvor.test", user_metadata: { name: "Kahvor QA" } };
  } else {
    try {
      const sessionResult = await supabase.auth.getSession();
      if (sessionResult.error) throw sessionResult.error;
      session = sessionResult.data.session;
      verifiedUser = session?.user || null;
    } catch (error) {
      authScreen({ error: error.message || "Sign-in could not be completed." });
    }
  }

  applyAppearance(loadAppearance(globalThis.localStorage, {
    userId: verifiedUser?.id || "",
    legacySettings: (() => {
      try { return JSON.parse(localStorage.getItem("kahvor:settings") || "{}"); } catch { return {}; }
    })()
  }));

  const recoveryRoute = location.pathname.startsWith("/auth/recovery");
  if (recoveryRoute) {
    if (!session) {
      bindAuthScreen(supabase, "recover", { error: "This recovery link is invalid or expired. Request a new one." });
    } else {
      bindAuthScreen(supabase, "update");
    }
    return;
  }

  if (!session && !preview) {
    const params = new URLSearchParams(location.search);
    bindAuthScreen(supabase, params.get("mode") === "create" ? "create" : "signin", {
      error: params.get("error_description") || ""
    });
    return;
  }

  if (
    location.pathname.startsWith("/auth/callback")
    || location.pathname.startsWith("/auth/provider-complete")
    || location.pathname.startsWith("/auth/confirmed")
    || new URLSearchParams(location.search).has("auth")
  ) cleanAuthUrl({ preserveNative: Boolean(nativeHandler) });
  window.KAHVOR_RUNTIME_CONFIG = {
    apiBase: API_BASE,
    accessToken: session?.access_token || "",
    providerReturnUrl: nativeHandler
      ? `kahvor-${nativePlatform}://auth/provider-complete/`
      : `${location.origin}/auth/provider-complete/`,
    user: verifiedUser,
    nativePlatform,
    notifications: config.notifications || {},
    preview
  };
  window.KAHVOR_AUTH = {
    async signOut() {
      await window.__kahvorClearRuntimeCaches?.();
      await supabase.auth.signOut();
      sessionStorage.removeItem("kahvor:backend:session");
      location.assign("/");
    },
    getUser: () => verifiedUser
  };

  installMailRealtimeBridge(supabase, verifiedUser?.id, session?.access_token);

  supabase.auth.onAuthStateChange((event, nextSession) => {
    const accessToken = nextSession?.access_token || "";
    if (event === "INITIAL_SESSION" && !accessToken && window.KAHVOR_RUNTIME_CONFIG.accessToken) return;
    window.KAHVOR_RUNTIME_CONFIG.accessToken = accessToken;
    window.KAHVOR_REALTIME?.setAccessToken(accessToken);
    window.dispatchEvent(new CustomEvent("kahvor:auth-token", { detail: { accessToken, event } }));
  });

  await import("../app.js");
}

bootstrap().catch((error) => unavailableScreen(error.message || "Kahvor could not start."));
